import { PropsWithChildren, useCallback, useLayoutEffect } from 'react';
import {
  NavigateOptions,
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router';
import { useBoolean, useLocalStorage } from 'usehooks-ts';

import { LS_USER_BLOCKED_AT_PATH } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useApplicationStatuses } from '@/hooks/useApplicationStatuses';
import { useConfirmAdultPickup } from '@/hooks/useConfirmAdultPickup';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectLivenessrecordDisabled,
  selectNewApplicationTypeSelectionFeature,
  selectRightStartFeature,
} from '@/redux/configs';

import { getRelativeUrl } from '@/utils/domain';
import { isRemediationCustomer } from '@/utils/user';

/**
 * Routes where the page handles its own navigation explicitly.
 * ProtectedRoute will skip the automatic redirect chain for these routes,
 * allowing the page component to call navigate() when ready.
 */
const SELF_MANAGED_ROUTES = new Set([
  ROUTE.BASIC_INFO,
  ROUTE.MINOR_BASIC_INFO,
  ROUTE.VERIFY_MINOR_BIRTH_CERTIFICATE,
  ROUTE.VERIFY_MINOR_FAMILY_BOOK,
  ROUTE.VERIFY_MINOR_ID,
  ROUTE.UPDATE_PERSONAL_DETAILS,
]);

export function ProtectedRoute({ children }: Readonly<PropsWithChildren>) {
  const navigate = useNavigate();
  const [userBlockedAtPath] = useLocalStorage(LS_USER_BLOCKED_AT_PATH, '');
  const location = useLocation();
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const enabledNewApplicationTypeSelection = useAppSelector(
    selectNewApplicationTypeSelectionFeature,
  );
  const user = useAppSelector(selectLoggedInUser);
  const { notNavigateByStatus } = location.state || {};
  const appStatuses = useApplicationStatuses();
  useConfirmAdultPickup();
  const { value: isRouteChecked, setTrue: setRouteChecked } = useBoolean(false);
  const isRecordDisabled = useAppSelector(selectLivenessrecordDisabled);
  const navigationType = useNavigationType();
  /*
   * All protected routes render <ProtectedRoute> at the same position in the
   * element tree, so React keeps this component mounted across navigations and
   * the redirect chain below would only re-run when appStatuses change.
   * On browser back/forward (POP) the statuses are unchanged, so we use the
   * location key as an extra trigger to re-evaluate the chain and prevent
   * users from navigating back to already-completed steps.
   */
  const popLocationKey =
    navigationType === NavigationType.Pop ? location.key : '';

  const navigateByStatus = useCallback(
    (url: string, options: NavigateOptions) => {
      const relativeUrl = getRelativeUrl();
      if (notNavigateByStatus || relativeUrl.pathname === url) {
        return;
      }
      navigate(url, options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appStatuses, notNavigateByStatus],
  );

  useLayoutEffect(() => {
    const {
      isAddressConfirmed,
      isPhoneConfirmed,
      isIDChecked,
      isLivenessVideoRecorded,
      isSelfieChecked,
      isAccountTypeSelected,
      isInfoConfirmed,
      isICBReady,
      isRejected,
      isAgeRestriction,
      isJointAdultAgeRestriction,
      isMinorAgeRestriction,
      isThirdPartyQuestionAnswered,
      isThirdPartyComplianceRejected,
      isCaymanCountryNotSelected,
      isCaymanThirdPartyComplianceRejected,
      isApplicationTypeSelected,
      isMinorFilledAdultDetail,
      isMinorFilledAdultContact,
      isMinorIDChecked,
      isMinorInfoConfirmed,
      isMinorEkycStarted,
      isNonResidentMinorRejected,
    } = appStatuses;

    // Pages in SELF_MANAGED_ROUTES handle their own navigation explicitly,
    // so skip the automatic redirect chain for them.
    const currentPath = getRelativeUrl().pathname;
    if (SELF_MANAGED_ROUTES.has(currentPath)) {
      setRouteChecked();
      return;
    }

    if (userBlockedAtPath) {
      navigateByStatus(userBlockedAtPath, { replace: true });
    } else if (isNonResidentMinorRejected) {
      navigateByStatus(ROUTE.NON_RESIDENT_MINOR_REJECTED, { replace: true });
    } else if (isCaymanThirdPartyComplianceRejected) {
      navigateByStatus(ROUTE.CAYMAN_THIRD_PARTY_COMPLIANCE_CHECK_FAILED, {
        replace: true,
      });
    } else if (isThirdPartyComplianceRejected) {
      navigateByStatus(ROUTE.THIRD_PARTY_COMPLIANCE_CHECK_FAILED, {
        replace: true,
      });
    } else if (isAgeRestriction) {
      navigateByStatus(ROUTE.AGE_VALIDATION_FAILURE, { replace: true });
    } else if (enabledRightStartAccount && isJointAdultAgeRestriction) {
      navigateByStatus(ROUTE.ADD_ADULT_CONTACT, { replace: true });
    } else if (enabledRightStartAccount && isMinorAgeRestriction) {
      navigateByStatus(ROUTE.MINOR_AGE_VALIDATION_FAILURE, {
        replace: true,
      });
    } else if (isCaymanCountryNotSelected) {
      navigateByStatus(ROUTE.CAYMAN_COUNTRY_VALIDATION_FAILURE, {
        replace: true,
      });
    } else if (isRejected) {
      navigateByStatus(ROUTE.ACCOUNT_REJECTED, { replace: true });
    } else if (isRemediationCustomer(user) && !user?.confirmPersonalDetails) {
      navigateByStatus(ROUTE.REVIEW_PERSONAL_DETAILS, { replace: true });
    } else if (!isPhoneConfirmed) {
      navigateByStatus(ROUTE.PHONE_FORM, { replace: true });
    } else if (
      enabledNewApplicationTypeSelection &&
      !isApplicationTypeSelected
    ) {
      navigateByStatus(ROUTE.APPLICATION_TYPE_SELECTION, { replace: true });
    } else if (enabledRightStartAccount && isMinorFilledAdultDetail) {
      navigateByStatus(ROUTE.ADD_ADULT_DETAIL_RESULT, { replace: true });
    } else if (enabledRightStartAccount && isMinorFilledAdultContact) {
      navigateByStatus(ROUTE.ADD_ADULT_CONTACT, { replace: true });
    } else if (
      !isThirdPartyQuestionAnswered &&
      !enabledNewApplicationTypeSelection
    ) {
      navigateByStatus(ROUTE.THIRD_PARTY_COMPLIANCE_CHECK, { replace: true });
    } else if (!isIDChecked) {
      navigateByStatus(ROUTE.VERIFY_ID, { replace: true });
    } else if (
      !isSelfieChecked ||
      !(isRecordDisabled || isLivenessVideoRecorded)
    ) {
      navigateByStatus(ROUTE.VERIFY_SELFIE, { replace: true });
    } else if (enabledRightStartAccount && isMinorEkycStarted === false) {
      navigateByStatus(ROUTE.MORE_INFORMATION, { replace: true });
    } else if (enabledRightStartAccount && isMinorIDChecked === false) {
      navigateByStatus(ROUTE.VERIFY_MINOR_ID, { replace: true });
    } else if (enabledRightStartAccount && isMinorInfoConfirmed === false) {
      navigateByStatus(ROUTE.MINOR_BASIC_INFO, { replace: true });
    } else if (!isInfoConfirmed) {
      navigateByStatus(ROUTE.BASIC_INFO, { replace: true });
    } else if (!isAddressConfirmed) {
      navigateByStatus(ROUTE.ADDRESS_INFORMATION, { replace: true });
    } else if (!isICBReady) {
      navigateByStatus(ROUTE.SUCCESS, { replace: true });
    } else if (!isAccountTypeSelected) {
      navigateByStatus(ROUTE.ACCOUNT_TYPE, { replace: true });
    } else {
      navigateByStatus(ROUTE.MORE_INFORMATION, { replace: true });
    }

    setRouteChecked();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatuses, popLocationKey]);

  if (!isRouteChecked) return null;

  return children;
}
